import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';

/**
 * Unsaved-changes guard. BrowserRouter (no data router) has no useBlocker, and
 * all in-app navigation goes through the shell's nav TabList — so the shell
 * asks this context before navigating, and a `beforeunload` handler covers
 * reload / tab close. Screens with a dirty form register via
 * {@link useUnsavedGuard}.
 */

interface UnsavedGuardContextValue {
  /** Mark the active screen dirty/clean. Cleared automatically on unmount. */
  setDirty: (dirty: boolean) => void;
  /** True when navigation may proceed (prompts the user when dirty). */
  confirmLeave: () => boolean;
}

const UnsavedGuardContext = createContext<UnsavedGuardContextValue | null>(null);

export function UnsavedGuardProvider({ children }: { children: ReactNode }): JSX.Element {
  const dirtyRef = useRef<boolean>(false);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (dirtyRef.current) {
        e.preventDefault();
        // Chrome requires returnValue to be set for the native prompt.
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, []);

  const setDirty = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const confirmLeave = useCallback((): boolean => {
    if (!dirtyRef.current) {
      return true;
    }
    const leave = window.confirm('You have unsaved changes. Leave without saving?');
    if (leave) {
      dirtyRef.current = false;
    }
    return leave;
  }, []);

  const value = useMemo(() => ({ setDirty, confirmLeave }), [setDirty, confirmLeave]);

  return <UnsavedGuardContext.Provider value={value}>{children}</UnsavedGuardContext.Provider>;
}

/** Shell side: ask before navigating away. */
export function useConfirmLeave(): () => boolean {
  const ctx = useContext(UnsavedGuardContext);
  return ctx?.confirmLeave ?? ((): boolean => true);
}

/** Screen side: keep the guard in sync with the form's dirty flag. */
export function useUnsavedGuard(dirty: boolean): void {
  const ctx = useContext(UnsavedGuardContext);
  const setDirty = ctx?.setDirty;
  useEffect(() => {
    setDirty?.(dirty);
    return () => {
      setDirty?.(false);
    };
  }, [dirty, setDirty]);
}
